import { eventChannel, type EventChannel } from 'redux-saga';
import { all, call, fork, put, take, takeLatest } from 'redux-saga/effects';
import type { AuthIdentity, AuthRepository } from '@nx/domain';
import { isAuthError } from '@nx/shared';
import { authRepository } from '../../services/firebase';
import {
  authFailed,
  authSignedOut,
  authSucceeded,
  loginRequested,
  logoutRequested,
  registerRequested,
} from '../slices/authSlice';

function toMessage(error: unknown, fallback: string): string {
  if (isAuthError(error)) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function* handleLogin(
  repo: AuthRepository,
  action: ReturnType<typeof loginRequested>,
): Generator<unknown, void, unknown> {
  try {
    const identity = (yield call(
      [repo, repo.loginWithPassword],
      action.payload.email,
      action.payload.password,
    )) as AuthIdentity;
    yield put(authSucceeded({ identity, message: 'Login successful.' }));
  } catch (error) {
    yield put(authFailed({ message: toMessage(error, 'Login failed.') }));
  }
}

function* handleRegister(
  repo: AuthRepository,
  action: ReturnType<typeof registerRequested>,
): Generator<unknown, void, unknown> {
  try {
    const identity = (yield call(
      [repo, repo.registerCustomerOrPartner],
      action.payload,
    )) as AuthIdentity;
    yield put(authSucceeded({ identity, message: 'Registration successful.' }));
  } catch (error) {
    yield put(authFailed({ message: toMessage(error, 'Registration failed.') }));
  }
}

function* handleLogout(repo: AuthRepository): Generator<unknown, void, unknown> {
  try {
    yield call([repo, repo.signOut]);
    yield put(authSignedOut());
  } catch (error) {
    yield put(authFailed({ message: toMessage(error, 'Sign-out failed.') }));
  }
}

function createAuthChannel(repo: AuthRepository): EventChannel<AuthIdentity | null> {
  return eventChannel<AuthIdentity | null>((emit) => {
    const unsubscribe = repo.watchAuthState((identity) => emit(identity ?? null));
    return () => unsubscribe();
  });
}

function* watchAuthState(repo: AuthRepository): Generator<unknown, void, unknown> {
  const channel = (yield call(createAuthChannel, repo)) as ReturnType<
    typeof createAuthChannel
  >;
  while (true) {
    const identity = (yield take(channel)) as AuthIdentity | null;
    if (identity) {
      yield put(authSucceeded({ identity, message: null }));
    } else {
      yield put(authSignedOut());
    }
  }
}

export function* authSaga(repo: AuthRepository = authRepository) {
  yield all([
    takeLatest(loginRequested.type, handleLogin, repo),
    takeLatest(registerRequested.type, handleRegister, repo),
    takeLatest(logoutRequested.type, handleLogout, repo),
    fork(watchAuthState, repo),
  ]);
}
